import { createContext, useContext, useCallback } from 'react';
import { useTranslation as useTranslationI18n, Trans } from 'react-i18next';

// Context que define o namespace default da subtree. Cada app envolve
// sua árvore com `<I18nNamespaceProvider value="module-X">` uma vez —
// daí pra baixo, `useTranslation()` resolve as chaves contra esse ns
// sem que cada call-site precise declarar/repassar. Per-call override
// (`getTranslationAsString(key, { ns: 'Y' })`) continua funcionando.
const I18nNamespaceContext = createContext(null);

export const I18nNamespaceProvider = I18nNamespaceContext.Provider;

export const useTranslation = (overrideNs) => {
  const contextNs = useContext(I18nNamespaceContext);
  const ns = overrideNs || contextNs || undefined;
  const { t, i18n } = useTranslationI18n(ns);

  // O `ns` PRECISA ir como prop do <Trans>. O `t` acima já vem preso ao
  // namespace, mas o <Trans> não usa esse `t` nem enxerga o
  // I18nNamespaceContext — ele resolve a chave contra o `defaultNS` do i18next,
  // que o `initI18n` deixa como 'translations'. Sem esta prop, todo call-site
  // sob um <I18nNamespaceProvider> imprime a CHAVE CRUA na tela
  // ('page.auth.title' no lugar do título), sem erro no console.
  //
  // Quem escapava disso escapava por acidente: a landing e o dashboard chamam
  // `i18next.setDefaultNamespace(...)` no bootstrap porque embarcam um
  // namespace só. O onboarding não pode — divide a instância do i18next com o
  // host dentro da mesma página (Module Federation), e um default global
  // quebraria o outro.
  //
  // `ns` indefinido (app sem provider e sem override) mantém o comportamento
  // anterior: o <Trans> volta a cair no defaultNS.
  const getTranslation = useCallback(
    (key, interpolation = {}, components = {}, defaultValue = '') =>
      <Trans i18nKey={key} ns={ns} values={interpolation} components={components} defaultValue={defaultValue} />,
    [ns]
  );

  const getTranslationAsString = (key, interpolation = {}, defaultValue = '') => t(key, { ...interpolation, defaultValue });

  const hasTranslation = (key) => t(key) !== key;

  return {
    i18n,
    getTranslation,
    hasTranslation,
    getTranslationAsString,
  }

}
